import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown, ChevronUp, AlertTriangle, ShieldAlert, CheckCircle2, Building2, Trees, Users, Landmark } from "lucide-react";
import { authFetch } from "@/hooks/use-auth";

function fmt(val: number, options?: Intl.NumberFormatOptions) {
  return val.toLocaleString("pt-BR", options);
}

function fmtBRL(val: number) {
  return val.toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 });
}

function SemaforoCard({ semaforo, motivosBloqueante, motivosRestritivos }: {
  semaforo: "BLOQUEANTE" | "RESTRITIVO" | "SEM_ALERTAS";
  motivosBloqueante: string[];
  motivosRestritivos: string[];
}) {
  const config = {
    BLOQUEANTE: {
      bg: "bg-red-50 dark:bg-red-950 border-red-200 dark:border-red-800",
      title: "BLOQUEANTE",
      icon: <AlertTriangle className="h-6 w-6 text-red-600" />,
      label: "text-red-700 dark:text-red-400",
      dot: "🔴",
    },
    RESTRITIVO: {
      bg: "bg-amber-50 dark:bg-amber-950 border-amber-200 dark:border-amber-800",
      title: "RESTRITIVO",
      icon: <ShieldAlert className="h-6 w-6 text-amber-600" />,
      label: "text-amber-700 dark:text-amber-400",
      dot: "🟡",
    },
    SEM_ALERTAS: {
      bg: "bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800",
      title: "SEM ALERTAS",
      icon: <CheckCircle2 className="h-6 w-6 text-green-600" />,
      label: "text-green-700 dark:text-green-400",
      dot: "🟢",
    },
  };

  const c = config[semaforo];
  const motivos = semaforo === "BLOQUEANTE" ? motivosBloqueante : motivosRestritivos;

  return (
    <div className={`rounded-xl border-2 p-4 ${c.bg}`} data-testid="semaforo-card">
      <div className="flex items-center gap-3 mb-2">
        {c.icon}
        <div>
          <div className={`text-xl font-bold ${c.label}`}>{c.dot} {c.title}</div>
          <div className="text-xs text-muted-foreground">Resultado da análise de Due Diligence</div>
        </div>
      </div>
      {motivos.length > 0 && (
        <ul className="mt-3 space-y-1">
          {motivos.map((m, i) => (
            <li key={i} className="text-sm flex items-start gap-2">
              <span className="mt-0.5 shrink-0">•</span>
              <span>{m}</span>
            </li>
          ))}
        </ul>
      )}
      {motivos.length === 0 && semaforo === "SEM_ALERTAS" && (
        <p className="text-sm text-green-700 dark:text-green-400 mt-2">
          Nenhuma restrição ambiental, fundiária ou fiscal identificada para este processo.
        </p>
      )}
    </div>
  );
}

function Section({ title, icon, badge, badgeVariant, children, defaultOpen = false }: {
  title: string;
  icon: React.ReactNode;
  badge?: string;
  badgeVariant?: "destructive" | "secondary" | "outline" | "default";
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger
        className="w-full flex items-center justify-between p-3 rounded-lg border hover:bg-muted/50 transition-colors"
        data-testid={`section-trigger-${title.toLowerCase().replace(/\s/g, "-")}`}
      >
        <div className="flex items-center gap-2">
          {icon}
          <span className="font-medium text-sm">{title}</span>
          {badge !== undefined && (
            <Badge variant={badgeVariant || "outline"} className="text-xs">{badge}</Badge>
          )}
        </div>
        {open ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-2 space-y-2 px-1">
        {children}
      </CollapsibleContent>
    </Collapsible>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-4 py-1 border-b last:border-0 text-sm">
      <span className="text-muted-foreground shrink-0">{label}</span>
      <span className="font-medium text-right">{value}</span>
    </div>
  );
}

function IbamaSection({ ibama }: { ibama: any }) {
  const isAlert = ibama.temEmbargoAtivo || ibama.totalAutuacoes > 0;
  return (
    <Section
      title="IBAMA — Autuações e Embargos"
      icon={<Building2 className="h-4 w-4 text-orange-600" />}
      badge={ibama.temEmbargoAtivo ? "Embargo Ativo" : `${ibama.totalAutuacoes} autuações`}
      badgeVariant={ibama.temEmbargoAtivo ? "destructive" : ibama.totalAutuacoes > 0 ? "secondary" : "outline"}
      defaultOpen={isAlert}
    >
      <Card>
        <CardContent className="pt-3 pb-2 space-y-1">
          <Row label="Total de autuações" value={fmt(ibama.totalAutuacoes)} />
          <Row label="Valor total de multas" value={fmtBRL(ibama.valorTotalMultas)} />
          <Row label="Embargo ativo" value={
            ibama.temEmbargoAtivo
              ? <Badge variant="destructive" className="text-xs">SIM</Badge>
              : <Badge variant="outline" className="text-xs">Não</Badge>
          } />
          <Row label="Gravidade máxima" value={ibama.gravidadeMaxima || "—"} />
        </CardContent>
      </Card>

      {ibama.embargos.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Embargos</p>
          {ibama.embargos.map((e: any, i: number) => (
            <Card key={i} className="border-red-200 dark:border-red-800">
              <CardContent className="pt-3 pb-2 space-y-1">
                <Row label="Nº Termo" value={e.num_termo || "—"} />
                <Row label="Situação" value={<Badge variant={e.situacao?.toLowerCase().includes("cancel") ? "outline" : "destructive"} className="text-xs">{e.situacao || "—"}</Badge>} />
                <Row label="Data início" value={e.data_embargo_inicio || "—"} />
                <Row label="Município/UF" value={`${e.municipio || "—"} / ${e.uf || "—"}`} />
                <Row label="Área (ha)" value={e.area_ha ? fmt(e.area_ha, { maximumFractionDigits: 0 }) : "—"} />
                {e.descricao && <p className="text-xs text-muted-foreground pt-1">{e.descricao}</p>}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {ibama.autuacoes.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
            Principais Autuações {ibama.totalAutuacoes > 10 ? `(top 10 de ${fmt(ibama.totalAutuacoes)})` : ""}
          </p>
          {ibama.autuacoes.map((a: any, i: number) => (
            <Card key={i}>
              <CardContent className="pt-3 pb-2 space-y-1">
                <Row label="Nº Auto" value={a.num_auto_infracao || "—"} />
                <Row label="Data" value={a.data_infracao || "—"} />
                <Row label="Valor multa" value={fmtBRL(parseFloat(a.valor_multa || 0))} />
                <Row label="Gravidade" value={
                  <Badge variant={a.gravidade?.toUpperCase() === "GRAVÍSSIMA" || a.gravidade?.toUpperCase() === "GRAVE" ? "destructive" : "secondary"} className="text-xs">
                    {a.gravidade || "—"}
                  </Badge>
                } />
                <Row label="Situação" value={a.situacao || "—"} />
                <Row label="Município/UF" value={`${a.municipio || "—"} / ${a.uf || "—"}`} />
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {ibama.totalAutuacoes === 0 && ibama.embargos.length === 0 && (
        <p className="text-sm text-muted-foreground text-center py-3">
          Nenhuma autuação ou embargo encontrado para o titular
        </p>
      )}
      <p className="text-xs text-muted-foreground">
        Consulta realizada em: {new Date(ibama.consultadoEm).toLocaleString("pt-BR")}
      </p>
    </Section>
  );
}

function IcmbioSection({ icmbio }: { icmbio: any }) {
  return (
    <Section
      title="ICMBio — Unidades de Conservação"
      icon={<Trees className="h-4 w-4 text-green-700" />}
      badge={icmbio.temSobreposicao ? `${icmbio.ucs.length} UC${icmbio.ucs.length !== 1 ? "s" : ""}` : "Sem sobreposição"}
      badgeVariant={icmbio.bloqueante ? "destructive" : icmbio.temSobreposicao ? "secondary" : "outline"}
      defaultOpen={icmbio.bloqueante}
    >
      {icmbio.temSobreposicao ? (
        <div className="space-y-2">
          {icmbio.ucs.map((u: any, i: number) => (
            <Card key={i} className={u.grupo === "PROTECAO_INTEGRAL" ? "border-red-200 dark:border-red-800" : ""}>
              <CardContent className="pt-3 pb-2 space-y-1">
                <div className="font-medium text-sm">{u.nome}</div>
                <Row label="Categoria" value={u.categoria || "—"} />
                <Row label="Grupo" value={
                  <Badge variant={u.grupo === "PROTECAO_INTEGRAL" ? "destructive" : "secondary"} className="text-xs">
                    {u.grupo === "PROTECAO_INTEGRAL" ? "Proteção Integral" : u.grupo === "USO_SUSTENTAVEL" ? "Uso Sustentável" : u.grupo || "—"}
                  </Badge>
                } />
                <Row label="Esfera" value={u.esfera || "—"} />
                <Row label="UF" value={u.uf || "—"} />
                <Row label="Área (ha)" value={u.area_ha ? fmt(u.area_ha, { maximumFractionDigits: 0 }) : "—"} />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground text-center py-3">
          Sem sobreposição com Unidades de Conservação
        </p>
      )}
      <p className="text-xs text-muted-foreground">
        Consulta realizada em: {new Date(icmbio.consultadoEm).toLocaleString("pt-BR")}
      </p>
    </Section>
  );
}

function FunaiSection({ funai }: { funai: any }) {
  return (
    <Section
      title="FUNAI — Terras Indígenas"
      icon={<Users className="h-4 w-4 text-amber-700" />}
      badge={funai.temSobreposicao ? `${funai.tis.length} TI${funai.tis.length !== 1 ? "s" : ""}` : "Sem sobreposição"}
      badgeVariant={funai.bloqueante ? "destructive" : funai.temSobreposicao ? "secondary" : "outline"}
      defaultOpen={funai.bloqueante}
    >
      {funai.temSobreposicao ? (
        <div className="space-y-2">
          {funai.tis.map((t: any, i: number) => (
            <Card key={i} className={funai.bloqueante ? "border-red-200 dark:border-red-800" : ""}>
              <CardContent className="pt-3 pb-2 space-y-1">
                <div className="font-medium text-sm">{t.nome_ti}</div>
                <Row label="Fase" value={
                  <Badge variant={["Regularizada","Homologada","Declarada"].includes(t.fase_ti || "") ? "destructive" : "secondary"} className="text-xs">
                    {t.fase_ti || "—"}
                  </Badge>
                } />
                <Row label="Modalidade" value={t.modalidade || "—"} />
                <Row label="Etnia" value={t.etnia || "—"} />
                <Row label="Município/UF" value={`${t.municipio || "—"} / ${t.uf || "—"}`} />
                <Row label="Superfície (ha)" value={t.superficie_ha ? fmt(t.superficie_ha, { maximumFractionDigits: 0 }) : "—"} />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground text-center py-3">
          Sem sobreposição com Terras Indígenas
        </p>
      )}
    </Section>
  );
}

function PgfnSection({ pgfn }: { pgfn: any }) {
  return (
    <Section
      title="PGFN — Dívida Ativa da União"
      icon={<Landmark className="h-4 w-4 text-blue-700" />}
      badge={pgfn.hasAtivaDivida ? "Dívida Ativa" : "Sem dívida"}
      badgeVariant={pgfn.hasAtivaDivida ? "destructive" : "outline"}
      defaultOpen={pgfn.hasAtivaDivida}
    >
      <Card>
        <CardContent className="pt-3 pb-2 space-y-1">
          <Row label="Possui dívida ativa" value={
            pgfn.hasAtivaDivida
              ? <Badge variant="destructive" className="text-xs">SIM</Badge>
              : <Badge variant="outline" className="text-xs">Não</Badge>
          } />
          <Row label="Total de inscrições" value={fmt(pgfn.totalRegistros)} />
          <Row label="Valor total consolidado" value={fmtBRL(pgfn.valorTotalDivida)} />
          <Row label="Tipos de dívida" value={pgfn.tiposDivida.length > 0 ? pgfn.tiposDivida.join(", ") : "—"} />
        </CardContent>
      </Card>
      <p className="text-xs text-muted-foreground">
        Consulta realizada em: {new Date(pgfn.consultadoEm).toLocaleString("pt-BR")}
      </p>
    </Section>
  );
}

export function AnmDueDiligenceTab({ processo, titular }: { processo: string; titular?: string }) {
  const ddQ = useQuery({
    queryKey: ["/api/anm/processos", processo, "due-diligence"],
    queryFn: () => authFetch(`/api/anm/processos/${encodeURIComponent(processo)}/due-diligence`),
    enabled: !!processo,
    staleTime: 5 * 60 * 1000,
  });

  if (ddQ.isLoading) {
    return (
      <div className="space-y-3 py-2">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    );
  }

  if (ddQ.isError || !ddQ.data) {
    return (
      <div className="py-6 text-center">
        <p className="text-sm text-muted-foreground">
          Erro ao carregar dados de due diligence.
        </p>
      </div>
    );
  }

  const { semaforo, motivosBloqueante, motivosRestritivos, dueDiligence, temGeometria } = ddQ.data as any;

  return (
    <div className="space-y-3 py-1">
      {!temGeometria && (
        <p className="text-xs text-amber-600 bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded px-3 py-2">
          Este processo não possui geometria cadastrada. Análises de sobreposição espacial (ICMBio, FUNAI) não disponíveis.
        </p>
      )}

      <SemaforoCard
        semaforo={semaforo}
        motivosBloqueante={motivosBloqueante}
        motivosRestritivos={motivosRestritivos}
      />

      <div className="space-y-2">
        <IbamaSection ibama={dueDiligence.ibama} />
        <IcmbioSection icmbio={dueDiligence.icmbio} />
        <FunaiSection funai={dueDiligence.funai} />
        <PgfnSection pgfn={dueDiligence.pgfn} />
      </div>

      <p className="text-xs text-muted-foreground pt-1">
        * Busca por nome do titular "{titular || "—"}". Resultados podem incluir homônimos.
      </p>
    </div>
  );
}
